import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, filter, Observable, switchMap, tap } from 'rxjs';
import { environment } from '@env/environment';
import { NewUser, Preferences, UserModel } from '@rillion/auth/models';
import { AppSettingsService, AppStorage } from '@rillion/core';
import { RoleModel } from 'apps/rillion-ui/src/libs/role';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { AuthActions } from '@rillion/store/auth/auth.actions';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  public isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  private userModelSubject$ = new BehaviorSubject<UserModel>(null);
  public userModel$ = this.userModelSubject$.asObservable();

  constructor(
    private http: HttpClient,
    private appSettingsService: AppSettingsService,
    private readonly store: Store,
    private readonly oauthService: OAuthService,
    private readonly router: Router,
    private readonly httpBackend: HttpBackend,
    private readonly cookieService: CookieService,
    private readonly appStorage: AppStorage,
  ) {
    if (!environment.production) {
      this.oauthService.events.pipe(filter((event) => event.type !== 'session_unchanged')).subscribe((event) => {
        if (event instanceof OAuthErrorEvent) {
          console.error('OAuthErrorEvent Object:', event);
        } else {
          console.warn('OAuthEvent Object:', event);
        }
      });
    }

    // The following cross-tab communication of fresh access tokens works usually in practice,
    // but if you need more robust handling the community has come up with ways to extend logic
    // in the library which may give you better mileage.
    //
    // See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    //
    // Until then we'll stick to this:
    window.addEventListener('storage', (event) => {
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }
      console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

      if (!this.oauthService.hasValidAccessToken()) {
        this.login();
      }
    });

    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
  }

  public initialLogin(): Promise<void> {
    this.oauthService.setupAutomaticSilentRefresh();
    return this.oauthService
      .loadDiscoveryDocumentAndLogin({
        onTokenReceived: (context) => {
          this.router.navigateByUrl(context.state);
        },
      })
      .then(() => {
        const cookieRole = JSON.parse(atob(this.cookieService.get('authenticationToken')?.split('.')[1] || btoa('{}')))?.Role;
        const localRole = this.appStorage.loadDataSync<string>('role');
        const role = localRole || cookieRole;
        this.generateAccessToken(role);
      });
  }

  generateAccessToken(role: string | null) {
    const http = new HttpClient(this.httpBackend);
    const user: string = this.oauthService.getIdentityClaims()['name'];
    const headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', `Bearer ${this.oauthService.getAccessToken()}`);

    http
      .post<{ id_token: string }>(
        `${this.appSettingsService.appSettings.baseUrl}/api/v1.0/authorize`,
        { user, role: role || `dummy-role-${btoa(user)}`, tenantId: this.appSettingsService.appSettings.tenantId },
        { headers },
      )
      .pipe(
        switchMap((result) => {
          const unpackedToken = JSON.parse(atob(result.id_token.split('.')[1]));
          const datePipe = new DatePipe(unpackedToken.languageCode);
          const headers = new HttpHeaders().set('Content-Type', 'application/json').set('Authorization', `Bearer ${result.id_token}`);
          return this.http.get<Preferences>(`${this.appSettingsService.appSettings.baseUrl}/api/v1.0/preferences`, { headers }).pipe(
            tap((userPreferences) => {
              const user: UserModel = {
                user: unpackedToken.LoginName,
                role: !role ? '' : unpackedToken.Role, // Hide the dummy role from the user
                languageCode: userPreferences.languageCode || 'en-GB',
                token: result.id_token,
                lastLogin: datePipe.transform(unpackedToken.LastLogin),
                environment: unpackedToken.Environment,
                email: unpackedToken.Email,
                rows: unpackedToken.Rows,
              };
              if (this.userModelSubject$.value === null) {
                this.userModelSubject$.next(user);
                this.store.dispatch(AuthActions.getUser());
              } else if (unpackedToken.HasDefaultRole === "true" || unpackedToken.Role.includes('dummy-role')) {
                this.store.dispatch(AuthActions.updateUserRole({ user }));
              }
              this.isDoneLoadingSubject$.next(true);
            }),
          );
        }),
      )
      .subscribe();
  }

  getRolesForUser(user: string): Observable<Array<RoleModel>> {
    const url = `${this.appSettingsService.appSettings.baseUrl}/api/v1.0/role?user=${user}&Keys=active&Operations=eq&Values=1`;
    return this.http.get<Array<RoleModel>>(url);
  }

  signUp(newUser: NewUser): Observable<UserModel> {
    const url = this.appSettingsService.appSettings.baseUrl + environment.api.auth.signup;
    return this.http.post<UserModel>(url, newUser);
  }

  public login(targetUrl?: string) {
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }

  public logout(): Promise<any> {
    return this.oauthService.revokeTokenAndLogout();
  }

  /** @deprecated Replaced with {@link userModel$} */
  getUserData(): Observable<UserModel> {
    return this.userModel$;
  }

  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }

  public refresh() {
    this.oauthService.silentRefresh();
  }
}
